import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { FieldPath, FormProvider, useForm } from 'react-hook-form';
import { FormFactoryNoLayout } from '../../../components/Form/FormFactory';
import { FundMetrics, FundMetricsByFund, fundMetricsFormSchema } from '../../../schemas/FundMetrics.schema';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { clientConfigFieldsAtom } from '../../../services/state/AppConfigStateJ';
import { FDMap } from '../../../util/data-structure/FDMap';
import { schemaToFormFields } from '../../../util/schema-utils';
import { createForm } from '../../../view-models/form.view-model';
import { UnstyledFieldset } from '../../CompanyProfiles/Scenarios/components/commonStyledComponents';
import { FieldsWrapper, usePanelPagination } from '../../CompanyProfiles2/Summary/CompanyDetailsPanel';
import { Panel } from '../../CompanyProfiles2/Summary/Panel';
import { getFundMetricsAdditionalData, useFundMetricsFP } from '../useFundMetricsFP';
import { FundCompanyData, fundCompanyDataFormSchema, useFundCompanyData } from './useFundCompanyData';
import { useLpNavOverride } from './useLpNavOverride';

const LpNavDesc =
  'Input a desired override for LP NAV balance for calculating LP Net Returns (Net IRR). Please note that the LP NAV balance will be reflected for the as-of the date in the Date Setting dropdown above';
// Net IRR (Total) and Net MOIC are omitted: neither metric is validated or externally reported.
const metricsFieldsNoLpOverride: FieldPath<FundMetrics>[] = [
  'capitalCalledPercent',
  'ttvpi',
  'dpi',
  'rvpi',
  'lpTvpi',
  'lpDpi',
  'lpNetIrr',
  'managementFee',
  'fmv',
  'realizedValue',
  'grossIRR',
  'grossMOIC',
  'investmentAmount',
  'distributions',
  'contributions',
  'lossRatio',
  'capitalLossRatio',
  'activationDate',
  'initialInvestmentAmount',
  'followOnInvestmentAmount',
  'realizedGL',
  'unrealizedGL',
];
const metricsFieldsWithLpNav = metricsFieldsNoLpOverride.toSpliced(
  metricsFieldsNoLpOverride.indexOf('lpNetIrr') + 1,
  0,
  'lpNav'
);

export function FundMetricsPanel() {
  const { metrics } = useFundMetricsFP();
  const fundCompanyData = useFundCompanyData();
  const { fields, onChange } = useFundMetricsPanelFields(metrics);

  const { paginationConfig, visibleFields } = usePanelPagination({
    fields,
    itemsPerPage: 10,
  });

  const methods = useForm({
    values: { ...(metrics?.metrics ?? {}), ...fundCompanyData },
  });

  if (!metrics) {
    return null;
  }

  const form = createForm<FundMetrics | FundCompanyData>({
    fields: FDMap.fromArray(visibleFields, 'key'),
    data: methods.getValues() ?? {},
    variant: 'form-inline',
  });

  return (
    <Panel title={'Fund Metrics'} paginationConfig={paginationConfig} showContentOverflow={true}>
      <UnstyledFieldset>
        <FieldsWrapper style={{ minHeight: '22rem' }}>
          <FormProvider {...methods}>
            <FormFactoryNoLayout form={form} onChange={onChange} />
          </FormProvider>
        </FieldsWrapper>
      </UnstyledFieldset>
    </Panel>
  );
}

export function useFundMetricsPanelFields(metrics?: FundMetricsByFund) {
  const allowFundWaterfall = useAtomValue(clientConfigFieldsAtom)?.Allow_Fund_Waterfall;
  const allowLpNavOverride =
    !allowFundWaterfall && PermissionService.get().hasPermission(PermissionKey.canEditTransaction);

  const fundCompanySchema = fundCompanyDataFormSchema();
  const fundCompanyFields = schemaToFormFields(fundCompanySchema);
  const metricsFields = allowFundWaterfall ? metricsFieldsNoLpOverride : metricsFieldsWithLpNav;
  const fundMetricsSchema = fundMetricsFormSchema().pick(metricsFields);
  const fundMetricsFields = schemaToFormFields(fundMetricsSchema, metricsFields);
  const additionalDataMap = useMemo(() => {
    if (!metrics) {
      return;
    }
    return getFundMetricsAdditionalData(metrics.metrics);
  }, [metrics]);

  const overrideLpNav = useLpNavOverride();

  const onChange = useCallback(
    (data: Partial<FundMetrics | FundCompanyData>) => {
      if (!allowLpNavOverride) {
        return;
      }
      const lpNav = (data as FundMetrics)?.lpNav;
      if (lpNav != null) {
        overrideLpNav(lpNav);
      }
    },
    [allowLpNavOverride, overrideLpNav]
  );

  const fields = useMemo(() => {
    const splitIdx = metricsFields.indexOf('distributions');
    return [
      ...fundMetricsFields.slice(0, splitIdx),
      ...fundCompanyFields,
      ...fundMetricsFields.slice(splitIdx),
    ].map((f) => {
      if (f.key === 'distributions') {
        const lpDistributions = additionalDataMap?.get('lpDistributions');
        const gpDistributions = additionalDataMap?.get('gpDistributions');
        return {
          ...f,
          required: false,
          description: `${lpDistributions?.label}: ${lpDistributions?.formattedValue} 
          ${gpDistributions?.label}: ${gpDistributions?.formattedValue}`,
          disabled: true,
        };
      }
      if (f.key === 'contributions') {
        const lpContributions = additionalDataMap?.get('lpContributions');
        const gpContributions = additionalDataMap?.get('gpContributions');
        return {
          ...f,
          required: false,
          description: `${lpContributions?.label}: ${lpContributions?.formattedValue}
          ${gpContributions?.label}: ${gpContributions?.formattedValue}`,
          disabled: true,
        };
      }

      if (f.key === 'lpNav') {
        return {
          ...f,
          required: false,
          disabled: !allowLpNavOverride,
          description: allowLpNavOverride ? LpNavDesc : undefined,
        };
      }

      return { ...f, required: false, disabled: true };
    });
  }, [additionalDataMap, allowLpNavOverride, fundCompanyFields, fundMetricsFields, metricsFields]);

  return { fields, onChange };
}
